import { CONVERSATIONS_API_URL, CONVERSATIONS_COUNTS_URL, CONVERSATIONS_FILTER_URL } from 'constants/ApiConstants';
import { CONVERSATION_TYPES } from 'constants/ChatConstants';
import { PaginationType } from 'constants/Global';
import { createDictionary } from 'utils/ObjectUtils';

import * as types from '../actionTypes';

/**
 * @param {string} dealerId
 * @param {Object} [meta={}]
 * @returns {import('../types').FetchUnreadCountsByTypeAction}
 */
export const fetchUnreadCountsByType = (dealerId, meta = {}) => ({
  type: types.FETCH_UNREAD_COUNTS_BY_TYPE,
  payload: {
    request: {
      method: 'GET',
      url: CONVERSATIONS_COUNTS_URL,
      params: {
        dealerId,
      },
    },
  },
  meta,
});

/**
 * @param {import('../types').FetchUnreadCountsByTypeSuccessPayload} data
 * @param {Object} [meta={}]
 * @returns {import('../types').FetchUnreadCountsByTypeSuccessAction}
 */
export const fetchUnreadCountsByTypeSuccess = (data, meta = {}) => ({
  type: types.FETCH_UNREAD_COUNTS_BY_TYPE_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import('../types').FetchUnreadCountsByTypeFailureAction}
 */
export const fetchUnreadCountsByTypeFailure = (message, meta = {}) => ({
  type: types.FETCH_UNREAD_COUNTS_BY_TYPE_FAILURE,
  payload: message,
  error: true,
  meta,
});

const ADDITIONAL_FIELDS = createDictionary(
  {
    [CONVERSATION_TYPES.EXTERNAL]: { exist: 'latestMessage.sentOn' },
  },
  {}
);

/**
 * @param {Object} params
 * @param {Object} [meta={}]
 * @returns {import("../types").GenericFetchConversationsAction}
 */
export const genericFetchConversations = (params = {}, meta = {}) => ({
  type: types.GENERIC_FETCH_CONVERSATIONS,
  payload: {
    request: {
      method: 'GET',
      url: CONVERSATIONS_FILTER_URL,
      params: {
        page: 0,
        types: CONVERSATION_TYPES.INTERNAL,
        paginationType: PaginationType.SLICE,
        projection: 'page',
        sort: 'latestMessage.sentOn,desc',
        ...params,
        // @ts-ignore
        ...ADDITIONAL_FIELDS[params.types],
      },
    },
  },
  meta: { ...meta, '@app-request': true },
});

/**
 * @param {Object} params
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationsAction}
 */
export const fetchConversations = (params, meta = {}) => ({
  ...genericFetchConversations(params, meta),
  type: types.FETCH_CONVERSATIONS,
  meta: { ...meta, type: params.types ?? CONVERSATION_TYPES.INTERNAL, },
});

/**
 * @param {import("modules/global/generic.types").PageContent<import("../types").Conversation>} payload
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationsSuccessAction}
 */
export const fetchConversationsSuccess = (payload, meta = {}) => ({
  type: types.FETCH_CONVERSATIONS_SUCCESS,
  payload,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchConversationsFailureAction}
 */
export const fetchConversationsFailure = (message, meta = {}) => ({
  type: types.FETCH_CONVERSATIONS_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {string} conversationId
 * @param {import('modules/global/typing/enums/message.status.enum').MessageStatusEnum} status
 * @param {Object} [meta={}]
 * @returns {import('../types').PatchConversationStatusAction}
 */
export const patchConversationStatus = (conversationId, status, meta = {}) => ({
  type: types.PATCH_CONVERSATION_STATUS,
  payload: {
    request: {
      method: 'PATCH',
      url: `${CONVERSATIONS_API_URL}/${conversationId}/messages`,
      data: { status },
    },
    conversationId,
  },
  meta,
});

/**
 * @param {import('../types').Conversation} conversation
 * @param {Object} [meta={}]
 * @returns {import('../types').PatchConversationStatusSuccessAction}
 */
export const patchConversationStatusSuccess = (conversation, meta = {}) => ({
  type: types.PATCH_CONVERSATION_STATUS_SUCCESS,
  payload: conversation,
  meta,
});

/**
 * @param {string} conversationId
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('../types').PatchConversationStatusFailureAction}
 */
export const patchConversationStatusFailure = (conversationId, error, meta = {}) => ({
  type: types.PATCH_CONVERSATION_STATUS_FAILURE,
  payload: {
    error,
    conversationId,
  },
  error: true,
  meta,
});

/**
 * @param {'EXTERNAL' | 'INTERNAL'} conversationType
 * @param {Object} [meta={}]
 * @returns {import('../types').MarkAllAsReadAction}
 */
export const markAllAsRead = (conversationType, meta = {}) => ({
  type: types.MARK_ALL_AS_READ,
  payload: {
    request: {
      method: 'PATCH',
      url: `${CONVERSATIONS_API_URL}/type/${conversationType}/messages`,
      data: {
        status: 'READ',
      },
    },
  },
  meta: {
    ...meta,
    '@app-request': true
  },
});

/**
 * @param {Object} [meta={}]
 * @returns {import("../types").ClearAllConversationAction}
 */
export const clearAllConversation = (meta = {}) => ({
  type: types.CLEAR_CONVERSATIONS,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("../types").ClearCustomerConversationsAction}
 */
export const clearCustomerConversations = (meta = {}) => ({
  type: types.CLEAR_CUSTOMER_CONVERSATIONS,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("../types").ClearInternalConversationsAction}
 */
export const clearInternalConversations = (meta = {}) => ({
  type: types.CLEAR_INTERNAL_CONVERSATIONS,
  meta,
});

/**
 * @param {{ mode: 'EXTERNAL' | 'INTERNAL' }} payload
 * @param {Object} [meta={}]
 * @returns {import('../types').OpenCreateConversationModalAction}
 */
export const openCreateConversationModal = (payload, meta = {}) => ({
  type: types.OPEN_CREATE_CONVERSATION_MODAL,
  payload,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('../types').CloseCreateConversationModalAction}
 */
export const closeCreateConversationModal = (meta = {}) => ({
  type: types.CLOSE_CREATE_CONVERSATION_MODAL,
  meta,
});

/**
 * @param {string} tab
 * @param {Object} [meta={}]
 * @returns {import('../types').SetActiveTabsAction}
 */
export const setActiveTabs = (tab, meta = {}) => ({
  type: types.SET_ACTIVE_TABS,
  payload: tab,
  meta,
});

/**
 * @param {boolean} value
 * @param {Object} [meta={}]
 * @returns {import("../types").SetInitializeAction}
 */
export const setInitialize = (value, meta = {}) => ({
  type: types.SET_INIT_CHAT,
  payload: value,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("../types").IsReadyAction}
 */
export const isReady = (meta = {}) => ({
  type: types.CHAT_IS_READY,
  meta,
});

/**
 * @param {import("../types").FilterData['field']} field
 * @param {import("../types").FilterData['value']} value
 * @param {Object} [meta={}]
 * @returns {import("../types").SetChatFilterAction}
 */
export const setChatFilter = (field, value, meta = {}) => ({
  type: types.SET_CHAT_FILTER,
  payload: { field, value },
  meta,
});

/**
 * @param {import("../types").FilterData[]} filters
 * @param {Object} [meta={}]
 * @returns {import("../types").SetChatFiltersAction}
 */
export const setChatFilters = (filters, meta = {}) => ({
  type: types.SET_CHAT_FILTERS,
  payload: filters,
  meta,
});

/**
 * @param {import("../types").Conversation} conversation
 * @param {Object} [meta={}]
 * @returns {import("../types").PrependConversationInListAction}
 */
export const prependConversationInList = (conversation, meta = {}) => ({
  type: types.PREPEND_CONVERSATION_IN_LIST,
  payload: conversation,
  meta,
});
